import { ADMIN_USER_ROLE_TIER } from '@shared/types';
import type { AdminGameModule } from '@admin/games/types';
import { CONTENT_MANAGER_ONLY } from '@admin/utils/adminAccess';
import QuestionsList from './questions/List';
import QuestionNew from './questions/New';
import QuestionEdit from './questions/Edit';
import QuestionQualityPage from './questions/Quality';
import QuestionReviewPage from './questions/Review';
import QuestionBankSection from './dashboard/QuestionBankSection';
import MatchRoundsSection from './matches/MatchRoundsSection';

/**
 * Word Clash's admin manifest (#817): the question bank (the `questions`
 * surface its server `GameModule.adminSurfaces` declares), the dashboard's
 * "Question bank" section and the match-detail rounds view. Registered in
 * `framework/admin/src/games/adminModules.ts`.
 */
export const wordClashAdminModule: AdminGameModule = {
  gameId: 'word-clash',
  surfaces: [
    {
      surface: 'questions',
      routes: [
        // Question quality: read-only metrics, open to ANY admin (viewer keeps
        // its read access; content_manager gets it as content) — the
        // layout's own gate, no tier wrapper.
        { path: 'questions/quality', Component: QuestionQualityPage },
        // Content management: content_manager + admin + superadmin. The
        // allowRoles admits the lateral role; minRole='admin' keeps viewer
        // (tier 1) out of content mutations, matching the server.
        { path: 'questions', Component: QuestionsList, minRole: 'admin', allowRoles: CONTENT_MANAGER_ONLY },
        { path: 'questions/review', Component: QuestionReviewPage, minRole: 'admin', allowRoles: CONTENT_MANAGER_ONLY },
        { path: 'questions/new', Component: QuestionNew, minRole: 'admin', allowRoles: CONTENT_MANAGER_ONLY },
        { path: 'questions/:id', Component: QuestionEdit, minRole: 'admin', allowRoles: CONTENT_MANAGER_ONLY },
      ],
      navItems: [
        // Questions + Review are content MUTATIONS — admin/superadmin via
        // tier, content_manager via roles, viewer excluded (server gating).
        {
          to: '/questions',
          label: 'Questions',
          group: 'Content',
          minTier: ADMIN_USER_ROLE_TIER.admin,
          roles: CONTENT_MANAGER_ONLY,
        },
        {
          to: '/questions/review',
          label: 'Question Review',
          group: 'Content',
          minTier: ADMIN_USER_ROLE_TIER.admin,
          roles: CONTENT_MANAGER_ONLY,
        },
        // Quality is read-only metrics — viewer+ via tier, plus content_manager.
        {
          to: '/questions/quality',
          label: 'Question Quality',
          group: 'Content',
          roles: CONTENT_MANAGER_ONLY,
        },
      ],
    },
  ],
  // content_manager has no dashboard; it works the question bank.
  landingPaths: { content_manager: '/questions' },
  dashboardSections: [QuestionBankSection],
  matchDetailSections: [MatchRoundsSection],
  auditTargetRoutes: {
    question: (id) => `/questions/${encodeURIComponent(id)}`,
  },
};
